import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { CrtImportHistoryButtons } from '@/components/CrtImportHistoryButtons'
import { CrtImportPeriodNotFound } from '@/components/CrtImportPeriodNotFound'
import { DetailsHeader } from '@/components/DetailsHeader'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import {
  ipAccrualByPeriodQueryOptions,
  ipAccrualsDetailsSearchSchema,
  useIpAccrualById,
  useUpdateIpAccrual,
} from '@/hooks/queries/crt/useIpAccruals'
import { useLastUpdatedIpAccrualsLog } from '@/hooks/queries/useAuditTrails'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { useTablePagination } from '@/hooks/useTablePagination'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { crtPeriodFromParams } from '@/lib/utils/crtPeriodParams'
import { queryClient } from '@/main'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import { UpdateIpAccrual } from '@repo/backend/crt'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IpAccrualDetailsDataTable from './-components/IpAccrualDetails/IpAccrualDetailsDataTable'
import { IpAccrualExportMenu } from './-components/IpAccrualExportMenu'

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/insights/ip-accruals/$year/$month',
)({
  component: IpAccrualPeriod,
  validateSearch: (search) => ipAccrualsDetailsSearchSchema.parse(search),
  loader: async ({ params }) => {
    // The URL names the import's period — its month of billing — not a CRT period.
    const period = crtPeriodFromParams(params)
    const ipAccrual = period
      ? await queryClient.fetchQuery(ipAccrualByPeriodQueryOptions(period))
      : null

    return {
      crumb: ipAccrual?.name ?? `${params.year}/${params.month}`,
      period,
      ipAccrualId: ipAccrual ? String(ipAccrual.id) : null,
    }
  },
})

function IpAccrualPeriod() {
  const { formatMonthYear } = useLocalizedDate()
  const { year, month } = Route.useParams()
  const { period, ipAccrualId } = Route.useLoaderData()

  if (!ipAccrualId) {
    return (
      <CrtImportPeriodNotFound
        period={period ? formatMonthYear(parseDateOnly(period)) : `${year}/${month}`}
        otherPeriodsTo="/crt/customer-investments/insights/ip-accruals"
      />
    )
  }

  return <IpAccrualDetails key={ipAccrualId} ipAccrualId={ipAccrualId} />
}

function IpAccrualDetails({ ipAccrualId }: { ipAccrualId: string }) {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonthYear } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { data, isPending, isError } = useIpAccrualById(ipAccrualId, {
    ...search,
    page: search.page ?? 1,
    pageSize,
  })
  const { data: lastUpdatedIpAccrualsTrail } = useLastUpdatedIpAccrualsLog(ipAccrualId)

  const [formState, setFormState] = useState({
    name: '',
    description: '',
  })

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
      })
    }
  }, [data])

  const getUnsavedChanges = (): UpdateIpAccrual | null => {
    if (!data) return null

    const changes: UpdateIpAccrual = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasUnsavedChanges = getUnsavedChanges() !== null

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedIpAccrualsTrail)

  const { mutate: updateIpAccrual, isPending: isUpdatePending } = useUpdateIpAccrual({
    ipAccrualId: Number(ipAccrualId),
  })

  const handleUpdateName = (name: string) => {
    setFormState((prev) => ({ ...prev, name }))
  }

  const handleUpdateDescription = (description: string) => {
    setFormState((prev) => ({ ...prev, description }))
  }

  const handleSave = () => {
    const unsavedChanges = getUnsavedChanges()
    if (!unsavedChanges) return

    updateIpAccrual(unsavedChanges)
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer maxWidth="wide">
        <Text>{t('errors.ipAccruals.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28" maxWidth="wide">
      <Block shouldBlock={hasUnsavedChanges} />

      <HStack gap="sm" className="justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={handleUpdateName}
          onUpdateDescription={handleUpdateDescription}
          permission="crt_ip_accruals_edit"
        />

        <VStack gap="md" className="items-end">
          <Text className="text-muted-foreground text-sm">
            {formatMonthYear(parseDateOnly(data.period))}
          </Text>
          <HStack gap="sm">
            <IpAccrualExportMenu
              ipAccrualId={ipAccrualId}
              search={search}
              detailsRowCount={data.detailsRowCount}
              rawRowCount={data.detailsRawRowCount}
            />
            <CrtImportHistoryButtons
              otherPeriodsTo="/crt/customer-investments/insights/ip-accruals"
              uploadType="import-ip-accruals-details"
              uploadAttributes={{ 'ip-accrual-id': ipAccrualId }}
            />
          </HStack>
        </VStack>
      </HStack>

      <IpAccrualDetailsDataTable
        ipAccrualId={ipAccrualId}
        ipAccrual={data}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <PermissionsGuard required={['crt_ip_accruals_edit']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdatePending}
          lastUpdatedLog={lastUpdatedLog}
          onSaveChanges={handleSave}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
